
import { defineComponent, PropType } from 'vue'
import { mapGetters, mapState } from 'vuex'
import { Diagram } from '@interfaces/DiagramTypes'
import { getDiagramViewUrl, getProfileUrl } from '@helpers/CommunityHelper'
import { UserType } from '@interfaces/UserTypes'
import { getUserJobAndLocation } from '@helpers/UserHelper'
import CommonDiagramCardNuxt from '~/components/common/CommonDiagramCardNuxt.vue'
import CommonContextMenu from '~/components/common/CommonContextMenu.vue'
import { Template } from '~/domain/interfaces/TemplateTypes'
import { DIAGRAM_ACTIONS } from '~/domain/constants/DiagramConst'
import { instanceOfDiagram } from '~/domain/helpers'
import AppActions from '~/domain/constants/AppActions'
import ContactDialog from '~/components/dialogs/ContactDialog.vue'

export default defineComponent({
  name: 'CommunityCardGrid',
  components: {
    CommonDiagramCardNuxt,
    CommonContextMenu,
    ContactDialog,
  },
  props: {
    /* Diagrams props */
    diagrams: {
      type: Array as PropType<(Diagram | Template)[]>,
      default: null,
    },
    id: {
      type: String,
      default: 'diagram-card-grid',
    },
    loadLazy: {
      type: Boolean,
      default: true,
    },
    /* Designers props */
    designers: {
      type: Array as PropType<UserType[]>,
      default: null,
    },
    /* Common props */
    starred: {
      type: Boolean,
      default: false,
    },
    maxPerRow: {
      type: String,
      default: 'auto',
    },
    exploreLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      listView: false,
      diagramActions: DIAGRAM_ACTIONS,
      menuVisible: false,
      menuPositionX: 0,
      menuPositionY: 0,
      menuDiagram: {} as Diagram,
      menuItems: [] as any,
      contactDialogVisible: false,
      contactDialogDesigner: {} as any,
    }
  },
  computed: {
    ...mapGetters({
      userIsAuthenticated: 'userIsAuthenticated',
      authenticatedUser: 'authenticatedUser',
    }),
    ...mapState('contextMenu', {
      visibleMenu: 'visibleMenu',
    }),
    cardColClasses(): string {
      switch (this.maxPerRow) {
        case '1':
          return 'col-12 pl-0 pr-0'
        case '3':
          return 'col-xxl-3 col-xl-4 col-lg-6 col-sm-6 col-12'
        case '4':
          return 'col-xxl-3 col-xl-3 col-lg-4 col-sm-6 col-12'
        default:
          return 'col-xxl-3 col-xl-3 col-lg-4 col-sm-6 col-12'
      }
    },
    appUrl(): string {
      return this.$config.apiBaseURL || ''
    },
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
    this.menuVisibleChanged(false)
  },
  methods: {
    /* Diagrams methods */
    getMenuItems(diagram?: Diagram | Template) {
      const isLiked =
        this.userIsAuthenticated &&
        this.authenticatedUser &&
        diagram &&
        instanceOfDiagram(diagram)
          ? (diagram as Diagram)?.likedByAuthenticatedUser
          : false

      return [
        {
          text: this.$t('wordCapitalOpen'),
          action: DIAGRAM_ACTIONS.open,
          visible: true,
        },
        {
          text: this.$t('openInNewTab'),
          action: DIAGRAM_ACTIONS.openNewTab,
          visible: true,
        },
        { text: null, action: null, visible: true }, // separator
        {
          text: this.$t('copyLink'),
          action: DIAGRAM_ACTIONS.copyLink,
          visible: true,
        },
        {
          text: this.$t('makeACopy'),
          action: DIAGRAM_ACTIONS.makeACopy,
          visible: true,
        },
        {
          text: isLiked
            ? this.$t('wordCapitalUnlike')
            : this.$t('wordCapitalLike'),
          action: isLiked ? DIAGRAM_ACTIONS.unlike : DIAGRAM_ACTIONS.like,
          visible: true,
        },
      ]
    },
    showMenu(evt: MouseEvent, diagram: Diagram | any) {
      if (
        this.visibleMenu[this.id] &&
        this.menuDiagram?.id &&
        diagram.id === this.menuDiagram.id
      ) {
        this.menuVisibleChanged(false)
      } else {
        this.menuDiagram = diagram
        this.menuPositionX = evt.clientX
        this.menuPositionY = evt.clientY + 10
        this.menuItems = this.getMenuItems(diagram)

        this.$store.commit('contextMenu/setIsUpdating', true)

        this.menuVisibleChanged(true)
      }
    },
    getDiagramViewUrl(diagram: Diagram | Template) {
      return getDiagramViewUrl(diagram)
    },
    menuVisibleChanged(visibility: boolean) {
      this.$store.commit('contextMenu/setIsVisible', {
        id: this.id,
        visible: visibility,
        target: this.menuDiagram,
      })
    },
    handleScroll() {
      /* Triggers the closing of the menu - which will close all menus (see implementation of 'setIsVisible') */
      this.menuVisibleChanged(false)
    },
    diagramAction(item: any): void {
      if (!item?.action || !item.diagram) return
      const diagramWebUrl = getDiagramViewUrl(this.menuDiagram as Diagram)
      switch (item.action) {
        case DIAGRAM_ACTIONS.like:
        case DIAGRAM_ACTIONS.unlike:
          this.likeDiagram(item)
          break
        case DIAGRAM_ACTIONS.open:
          window.open(diagramWebUrl, '_self')
          break
        case DIAGRAM_ACTIONS.openNewTab:
          window.open(diagramWebUrl, '_blank')
          break
        case DIAGRAM_ACTIONS.fork:
        case DIAGRAM_ACTIONS.makeACopy:
          window.open(item.diagram?.link, '_self')
          break
        case DIAGRAM_ACTIONS.copyLink:
          navigator.clipboard.writeText(
            (location && location.origin ? location.origin : '') + diagramWebUrl
          )
          break
        default:
          break
      }
    },
    async likeDiagram(item: any): Promise<void> {
      await this.$store.dispatch(AppActions.LIKE_DIAGRAM, {
        action: item.action,
        diagram: item.diagram,
        context: {
          ...this.$nuxt.context,
          $route: this.$route,
        },
      })
    },
    currentDiagramComponent(diagram: Diagram | Template) {
      return diagram?.showCTACard ? 'a' : 'nuxt-link'
    },
    currenDiagramProps(diagram: Diagram | Template) {
      return diagram?.showCTACard
        ? { href: this.$config.apiBaseURL, target: '_blank' }
        : { to: getDiagramViewUrl(diagram) }
    },

    currentDesignerComponent(designer: UserType) {
      return designer?.showCTACard ? 'a' : 'nuxt-link'
    },
    currenDesignerProps(designer: UserType) {
      return designer?.showCTACard
        ? { href: this.$config.apiBaseURL, target: '_blank' }
        : { to: getProfileUrl(designer) }
    },

    /* Designers methods */
    equalsAuthenticatedUser(designer) {
      if (this.$store.$auth.user && designer)
        return this.$store.$auth.user.username === designer.username
      else return false
    },
    jobAndLocation(job: string, location?: string): string {
      return getUserJobAndLocation(job, location)
    },
    async followUser(designer: UserType): Promise<void> {
      const isFollowed = !designer.isFollowed

      await this.$store.dispatch(AppActions.FOLLOW_DESIGNER, {
        designer,
        isFollowed,
        context: {
          ...this.$nuxt.context,
          $route: this.$route,
        },
      })
    },
    openContactDialog(diagramOwner: any) {
      this.contactDialogVisible = true
      this.contactDialogDesigner = diagramOwner

      this.$store.dispatch(AppActions.TRACK_SEGMENT_EVENT, {
        context: this,
        params: {
          name: 'Community - Get in touch',
          category: 'Community',
          action: 'Get in touch',
        },
      })
    },
  },
})
